'use client';
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { ROASTERS, REGIONS } from '@/lib/roastData';
import { Trophy, Flame, Search, Filter, ChevronUp, ChevronDown, Globe, Users } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

function Avatar({ initials, color, size = 36 }: { initials: string; color: string; size?: number }) {
  return (
    <div className="rounded-full flex items-center justify-center font-bold text-white flex-shrink-0"
      style={{ width: size, height: size, background: color, fontSize: size * 0.35 }}>
      {initials}
    </div>
  );
}

const TIME_FILTERS = ['All Time', 'This Month', 'This Week'];
const SORT_OPTIONS = [
  { id: 'points', label: 'Points' },
  { id: 'wins', label: 'Wins' },
  { id: 'winRate', label: 'Win Rate' },
  { id: 'streak', label: 'Streak' },
  { id: 'totalRoasts', label: 'Total Roasts' },
];

export default function LeaderboardPage() {
  const [timeFilter, setTimeFilter] = useState('All Time');
  const [regionFilter, setRegionFilter] = useState('world');
  const [sortBy, setSortBy] = useState('points');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const { t } = useLanguage();

  const filtered = useMemo(() => {
    let data = [...ROASTERS];
    if (regionFilter !== 'world') data = data.filter(r => r.region === regionFilter);
    if (search) data = data.filter(r =>
      r.displayName.toLowerCase().includes(search.toLowerCase()) ||
      r.username.toLowerCase().includes(search.toLowerCase()) ||
      r.country.toLowerCase().includes(search.toLowerCase())
    );
    data.sort((a, b) => {
      const av = a[sortBy as keyof typeof a] as number;
      const bv = b[sortBy as keyof typeof b] as number;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return data;
  }, [regionFilter, search, sortBy, sortDir]);

  const top3 = filtered.slice(0, 3);
  const rest = filtered.slice(3);

  const toggleSort = (col: string) => {
    if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const SortIcon = ({ col }: { col: string }) => (
    <span className="ml-1 inline-flex flex-col" style={{ color: sortBy === col ? 'var(--primary)' : 'var(--muted-foreground)' }}>
      {sortDir === 'desc' || sortBy !== col ? <ChevronDown size={10} /> : <ChevronUp size={10} />}
    </span>
  );

  const podiumConfigs = [
    { pos: 2, gradient: 'gradient-podium-silver', ring: 'avatar-ring-silver', medal: '🥈', color: 'var(--silver)', size: 52 },
    { pos: 1, gradient: 'gradient-podium-gold', ring: 'avatar-ring-gold', medal: '🥇', color: 'var(--gold)', size: 64 },
    { pos: 3, gradient: 'gradient-podium-bronze', ring: 'avatar-ring-bronze', medal: '🥉', color: 'var(--bronze)', size: 52 },
  ];

  return (
    <div className="min-h-screen pb-24 md:pb-8" style={{ background: 'var(--background)' }}>
      {/* Navbar */}
      <header className="sticky top-0 z-50 w-full" style={{ background: 'rgba(10,10,15,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border)' }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link href="/roastrank" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)' }}>
                <Flame size={18} className="text-white" />
              </div>
              <span className="font-bold text-lg" style={{ color: 'var(--foreground)' }}>Roast<span style={{ color: 'var(--primary)' }}>Rank</span></span>
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {[
                { label: t('rr_home'), href: '/roastrank' },
                { label: t('rr_leaderboard'), href: '/roastrank/leaderboard' },
                { label: t('rr_browse'), href: '/roastrank/browse' },
                { label: t('rr_submit'), href: '/roastrank/submit' },
                { label: '🏛️ Legacy', href: '/roastrank/legacy' },
              ].map(item => (
                <Link key={item.href} href={item.href} className="px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:bg-white/5"
                  style={{ color: item.href === '/roastrank/leaderboard' ? 'var(--primary)' : 'var(--muted-foreground)' }}>
                  {item.label}
                </Link>
              ))}
            </nav>
            <Link href="/roastrank/submit" className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: '#fff' }}>
              <Flame size={14} />
              {t('rr_submit_roast')}
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.15)' }}>
              <Trophy size={20} style={{ color: 'var(--gold)' }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Global Leaderboard</h1>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>The world's top roasters ranked by community votes</p>
            </div>
            <Link href="/roastrank/legacy" className="ml-auto flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 hover:scale-105"
              style={{ background: 'rgba(245,158,11,0.08)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.2)' }}>
              🏛️ Legacy Board
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="space-y-3 mb-8">
          {/* Time filters */}
          <div className="flex flex-wrap gap-2">
            {TIME_FILTERS.map(f => (
              <button key={f} onClick={() => setTimeFilter(f)}
                className="px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200"
                style={{
                  background: timeFilter === f ? 'rgba(34,197,94,0.15)' : 'var(--card)',
                  color: timeFilter === f ? 'var(--primary)' : 'var(--muted-foreground)',
                  border: timeFilter === f ? '1px solid rgba(34,197,94,0.3)' : '1px solid var(--border)',
                }}>
                {f}
              </button>
            ))}
          </div>

          {/* Region filters */}
          <div className="flex flex-wrap gap-2">
            {REGIONS.map(r => (
              <button key={r.id} onClick={() => setRegionFilter(r.id)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 flex items-center gap-1"
                style={{
                  background: regionFilter === r.id ? 'rgba(6,182,212,0.15)' : 'var(--card)',
                  color: regionFilter === r.id ? '#06b6d4' : 'var(--muted-foreground)',
                  border: regionFilter === r.id ? '1px solid rgba(6,182,212,0.3)' : '1px solid var(--border)',
                }}>
                <Globe size={10} />
                {r.label}
              </button>
            ))}
          </div>

          {/* Search + Sort */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
              <input
                type="text"
                placeholder="Search roasters, countries..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all duration-200"
                style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter size={14} style={{ color: 'var(--muted-foreground)' }} />
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Sort:</span>
              <div className="flex gap-1 flex-wrap">
                {SORT_OPTIONS.map(s => (
                  <button key={s.id} onClick={() => toggleSort(s.id)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 flex items-center"
                    style={{
                      background: sortBy === s.id ? 'rgba(34,197,94,0.15)' : 'var(--card)',
                      color: sortBy === s.id ? 'var(--primary)' : 'var(--muted-foreground)',
                      border: sortBy === s.id ? '1px solid rgba(34,197,94,0.3)' : '1px solid var(--border)',
                    }}>
                    {s.label}
                    <SortIcon col={s.id} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Podium Top 3 — only show when there are 3+ roasters */}
        {top3.length >= 3 && (
          <div className="mb-8">
            <div className="flex items-end gap-3 justify-center">
              {podiumConfigs.map((cfg) => {
                const roaster = top3[cfg.pos - 1];
                if (!roaster) return null;
                const rankChange = roaster.previousRank - roaster.globalRank;
                return (
                  <Link key={roaster.id} href={`/roastrank/profile/${encodeURIComponent(roaster.username)}`} className={`flex flex-col items-center gap-2 p-4 rounded-2xl ${cfg.gradient} transition-all duration-300 hover:scale-105 cursor-pointer flex-1 max-w-[200px]`}>
                    <div className="text-xl">{cfg.medal}</div>
                    <div className={cfg.ring + ' rounded-full'}>
                      <Avatar initials={roaster.initials} color={roaster.avatarColor} size={cfg.size} />
                    </div>
                    <div className="text-center w-full">
                      <div className="font-bold text-sm truncate" style={{ color: 'var(--foreground)' }}>{roaster.displayName}</div>
                      <div className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>{roaster.username}</div>
                      <div className="text-lg mt-1">{roaster.countryFlag} <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{roaster.country}</span></div>
                      <div className="font-mono font-bold text-base mt-1" style={{ color: cfg.color }}>{roaster.points.toLocaleString()}</div>
                      <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>pts</div>
                      {rankChange !== 0 && (
                        <div className="text-xs font-bold" style={{ color: rankChange > 0 ? 'var(--rise)' : 'var(--fall)' }}>
                          {rankChange > 0 ? `↑ +${rankChange}` : `↓ ${rankChange}`}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 justify-center">
                      {roaster.badges.slice(0, 1).map(b => (
                        <span key={b.id} className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: `${b.color}20`, color: b.color }}>
                          {b.icon} {b.label}
                        </span>
                      ))}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {filtered.length === 0 && (
          <div className="card-glass rounded-2xl p-16 text-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(34,197,94,0.1)', border: '2px dashed rgba(34,197,94,0.3)' }}>
              <Users size={32} style={{ color: 'var(--primary)' }} />
            </div>
            <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--foreground)' }}>
              {search ? 'No roasters found' : 'The throne is empty 👑'}
            </h2>
            <p className="text-base mb-2 max-w-md mx-auto" style={{ color: 'var(--muted-foreground)' }}>
              {search
                ? `No roasters match "${search}". Try a different search.`
                : 'No one has claimed the top spot yet. Submit a roast, earn votes, and be the first to enter the leaderboard.'}
            </p>
            {!search && (
              <>
                <p className="text-sm mb-8" style={{ color: 'var(--muted-foreground)' }}>
                  Every <span style={{ color: 'var(--primary)', fontWeight: 700 }}>upvote</span> on your roast earns you <span style={{ color: 'var(--primary)', fontWeight: 700 }}>10 points</span>. Climb the ranks. Become legendary.
                </p>
                <Link href="/roastrank/submit"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-base transition-all duration-300 hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)', color: '#fff', boxShadow: '0 0 30px rgba(34,197,94,0.3)' }}>
                  <Flame size={18} />
                  Be the First — Submit Your Roast
                </Link>
              </>
            )}
          </div>
        )}

        {/* Full Table — only when there are roasters */}
        {filtered.length > 0 && (
          <div className="card-glass rounded-2xl overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(255,255,255,0.02)' }}>
                    <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>Rank</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>Roaster</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>Country</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold cursor-pointer hover:text-foreground transition-colors" style={{ color: sortBy === 'points' ? 'var(--primary)' : 'var(--muted-foreground)' }} onClick={() => toggleSort('points')}>
                      Points <SortIcon col="points" />
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold cursor-pointer hover:text-foreground transition-colors" style={{ color: sortBy === 'wins' ? 'var(--primary)' : 'var(--muted-foreground)' }} onClick={() => toggleSort('wins')}>
                      Wins <SortIcon col="wins" />
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold cursor-pointer hover:text-foreground transition-colors" style={{ color: sortBy === 'winRate' ? 'var(--primary)' : 'var(--muted-foreground)' }} onClick={() => toggleSort('winRate')}>
                      Win Rate <SortIcon col="winRate" />
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold cursor-pointer hover:text-foreground transition-colors" style={{ color: sortBy === 'streak' ? 'var(--primary)' : 'var(--muted-foreground)' }} onClick={() => toggleSort('streak')}>
                      Streak <SortIcon col="streak" />
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((roaster, idx) => {
                    const rankChange = roaster.previousRank - roaster.globalRank;
                    return (
                      <tr key={roaster.id} className="transition-all duration-200 hover:bg-white/5" style={{ borderBottom: '1px solid var(--border)' }}>
                        <td className="px-4 py-3">
                          <span className="font-mono font-bold text-sm" style={{ color: idx < 3 ? 'var(--gold)' : 'var(--muted-foreground)' }}>
                            {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Link href={`/roastrank/profile/${encodeURIComponent(roaster.username)}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                            <Avatar initials={roaster.initials} color={roaster.avatarColor} size={36} />
                            <div>
                              <div className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{roaster.displayName}</div>
                              <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{roaster.username}</div>
                            </div>
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1.5 text-sm">
                            <span className="text-lg">{roaster.countryFlag}</span>
                            <span style={{ color: 'var(--muted-foreground)' }}>{roaster.country}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-bold text-sm" style={{ color: 'var(--primary)' }}>{roaster.points.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-bold text-sm" style={{ color: 'var(--foreground)' }}>{roaster.wins}</td>
                        <td className="px-4 py-3 text-right text-sm" style={{ color: 'var(--foreground)' }}>{roaster.winRate}%</td>
                        <td className="px-4 py-3 text-right">
                          <span className="flex items-center justify-end gap-1 px-2 py-1 rounded-lg" style={{ background: 'rgba(249,115,22,0.1)' }}>
                            <Flame size={12} style={{ color: '#f97316' }} />
                            <span className="text-xs font-bold" style={{ color: '#f97316' }}>{roaster.streak}d</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {rankChange === 0
                            ? <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>—</span>
                            : <span className="text-xs font-bold" style={{ color: rankChange > 0 ? 'var(--rise)' : 'var(--fall)' }}>
                                {rankChange > 0 ? `↑ +${rankChange}` : `↓ ${rankChange}`}
                              </span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y" style={{ borderColor: 'var(--border)' }}>
              {filtered.map((roaster, idx) => {
                const rankChange = roaster.previousRank - roaster.globalRank;
                return (
                  <Link key={roaster.id} href={`/roastrank/profile/${encodeURIComponent(roaster.username)}`}
                    className="flex items-center gap-3 px-4 py-3 transition-all duration-200 hover:bg-white/5 active:bg-white/10">
                    <div className="w-8 text-center">
                      <span className="font-mono font-bold text-sm" style={{ color: idx < 3 ? 'var(--gold)' : 'var(--muted-foreground)' }}>
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                      </span>
                    </div>
                    <Avatar initials={roaster.initials} color={roaster.avatarColor} size={40} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm truncate" style={{ color: 'var(--foreground)' }}>{roaster.displayName}</span>
                        <span className="text-base">{roaster.countryFlag}</span>
                      </div>
                      <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{roaster.username} · {roaster.country}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-mono font-bold text-sm" style={{ color: 'var(--primary)' }}>{roaster.points.toLocaleString()}</div>
                      <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>pts</div>
                      {rankChange !== 0 && (
                        <div className="text-xs font-bold" style={{ color: rankChange > 0 ? 'var(--rise)' : 'var(--fall)' }}>
                          {rankChange > 0 ? `↑${rankChange}` : `↓${Math.abs(rankChange)}`}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 md:hidden z-50" style={{ background: 'rgba(10,10,15,0.95)', backdropFilter: 'blur(20px)', borderTop: '1px solid var(--border)' }}>
        <div className="flex items-center justify-around py-2">
          {[
            { label: 'Home', href: '/roastrank', icon: '🏠' },
            { label: 'Rankings', href: '/roastrank/leaderboard', icon: '🏆' },
            { label: 'Submit', href: '/roastrank/submit', icon: '🔥' },
          ].map(item => (
            <Link key={item.href} href={item.href} className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all duration-200">
              <span className="text-xl">{item.icon}</span>
              <span className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
}
